import React, { useEffect, useMemo, useState } from "react";
import { MdAccountCircle, MdPayments, MdLock, MdEmail, MdVerified, MdBusiness, MdPhone, MdCardGiftcard } from "react-icons/md";
import { HiCheckCircle, HiXCircle } from "react-icons/hi2";
import { useSelector } from "react-redux";
import { isAxiosError } from "axios";
import Spinner from "../Components/Spinner";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import { RootState } from "../store";
import PassTemplate from "../Modals/PassTemplate";

type Plan = {
  id: string;
  name: string;
  price: number;
  duration: number;
  allowedLocations: number;
  allowedPasstemplates: number;
  allowedSubusers: number;
};

const InputField = ({
  label, type = "text", value, onChange, placeholder,
}: {
  label: string; type?: string; value: string;
  onChange: (v: string) => void; placeholder?: string;
}) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium text-gray-500">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
    />
  </div>
);

const SectionCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/60">
      <span className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center text-brand-600 flex-shrink-0">
        {icon}
      </span>
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Feedback = ({ error, success }: { error: string; success: string }) => (
  <>
    {error   && <p className="flex items-center gap-1.5 text-sm text-red-500"><HiXCircle className="w-4 h-4 flex-shrink-0" />{error}</p>}
    {success && <p className="flex items-center gap-1.5 text-sm text-emerald-600"><HiCheckCircle className="w-4 h-4 flex-shrink-0" />{success}</p>}
  </>
);

const SaveButton = ({ loading, label, onClick }: { loading: boolean; label: string; onClick: () => void }) => (
  <button
    disabled={loading}
    onClick={onClick}
    className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 active:scale-95 disabled:opacity-50 transition-all"
  >
    {loading ? "جارٍ الحفظ..." : label}
  </button>
);

const Settings: React.FC = () => {
  const [error, setError] = useState("");
  const [isloading, setIsloading] = useState(false);
  const [activeTab, setActiveTab] = useState<"account" | "plan">("account");
  const [plans, setPlans] = useState<Plan[] | null>(null);

  const org = useSelector((state: RootState) => state.auth.org);
  const organizationPlanId = org?.planId ?? null;

  const [renewLoading, setRenewLoading] = useState(false);

  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const [emailForm, setEmailForm] = useState({ newEmail: "", password: "" });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");

  const [passTemplates, setPassTemplates] = useState<PassTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [rewardForm, setRewardForm] = useState({
    rewardType: "Stamp" as "Stamp" | "CouponValue",
    couponValue: 25,
    currencyCode: "SAR",
    password: "",
  });
  const [rewardLoading, setRewardLoading] = useState(false);
  const [rewardError, setRewardError] = useState("");
  const [rewardSuccess, setRewardSuccess] = useState("");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await OrganizationAxiosInstance.get("/PassTemplate/All");
        const templates: PassTemplate[] = response.data.passTemplates;
        setPassTemplates(templates);
        if (templates.length > 0) {
          setSelectedTemplateId(templates[0].id);
          setRewardForm(f => ({
            ...f,
            rewardType: templates[0].rewardType || "Stamp",
            couponValue: templates[0].couponValue || 25,
            currencyCode: templates[0].currencyCode || "SAR",
          }));
        }
      } catch { /* non-critical for this section */ }
    };
    fetchTemplates();
  }, []);

  const handleSelectTemplate = (id: string) => {
    setSelectedTemplateId(id);
    const tmpl = passTemplates.find(p => p.id === id);
    setRewardForm(f => ({
      ...f,
      rewardType: tmpl?.rewardType || "Stamp",
      couponValue: tmpl?.couponValue || 25,
      currencyCode: tmpl?.currencyCode || "SAR",
    }));
    setRewardError(""); setRewardSuccess("");
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsloading(true);
        const response = await OrganizationAxiosInstance.get<Plan[]>("/Plans");
        if (response.status === 200) setPlans(response.data);
      } catch (e) {
        if (isAxiosError(e)) setError(e.message);
        else setError("Failed to load plans");
      } finally {
        setIsloading(false);
      }
    };
    fetchPlans();
  }, []);

  type TierKey = "Silver" | "Gold" | "Platinum" | "Free Trial";
  type TierGroup = {
    titleAr: string;
    tier: TierKey;
    monthly?: Plan;
    yearly?: Plan;
    featured?: boolean;
  };

  const toTierKey = (planName: string): TierKey | undefined => {
    const lower = planName.toLowerCase();
    if (lower.includes("silver"))   return "Silver";
    if (lower.includes("gold"))     return "Gold";
    if (lower.includes("platinum")) return "Platinum";
    if (lower.includes("free"))     return "Free Trial";
    return undefined;
  };

  const expiryDate = org?.subscriptionExpiry ? new Date(org.subscriptionExpiry) : null;
  const now = new Date();
  const isFreeTrialOrg = (org?.planName || "").toLowerCase().includes("free");
  const isExpired = expiryDate ? expiryDate.getTime() < now.getTime() : false;

  const grouped: TierGroup[] = useMemo(() => {
    const base: Record<TierKey, TierGroup> = {
      "Silver":     { titleAr: "الفضية",    tier: "Silver",     featured: true },
      "Gold":       { titleAr: "الذهبية",   tier: "Gold" },
      "Platinum":   { titleAr: "البلاتينية", tier: "Platinum" },
      "Free Trial": { titleAr: "تجريبية",   tier: "Free Trial" },
    };
    (plans || []).forEach((p) => {
      const tier = toTierKey(p.name);
      if (!tier) return;
      if (tier === "Free Trial" || p.duration < 100) base[tier].monthly = p;
      else base[tier].yearly = p;
    });
    return [base["Silver"], base["Gold"], base["Platinum"]];
  }, [plans]);


  const handleChangeEmail = async () => {
    setEmailError(""); setEmailSuccess("");
    if (!emailForm.newEmail || !emailForm.password) { setEmailError("يرجى تعبئة جميع الحقول."); return; }
    try {
      setEmailLoading(true);
      await OrganizationAxiosInstance.post("/Organization/change-email", {
        newEmail: emailForm.newEmail, password: emailForm.password,
      });
      setEmailSuccess("تم تغيير البريد الإلكتروني. يرجى التحقق من البريد الجديد.");
      setEmailForm({ newEmail: "", password: "" });
    } catch (e) {
      setEmailError(isAxiosError(e) ? (e.response?.data?.message ?? e.message) : "حدث خطأ.");
    } finally { setEmailLoading(false); }
  };

  const handleChangePassword = async () => {
    setPwError(""); setPwSuccess("");
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) { setPwError("يرجى تعبئة جميع الحقول."); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError("كلمة المرور الجديدة وتأكيدها غير متطابقتين."); return; }
    if (pwForm.next.length < 6) { setPwError("كلمة المرور يجب أن تكون 6 أحرف على الأقل."); return; }
    try {
      setPwLoading(true);
      await OrganizationAxiosInstance.post("/Organization/change-password", {
        currentPassword: pwForm.current, newPassword: pwForm.next,
      });
      setPwSuccess("تم تغيير كلمة المرور بنجاح.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      setPwError(isAxiosError(e) ? (e.response?.data?.message ?? e.message) : "حدث خطأ.");
    } finally { setPwLoading(false); }
  };

  const handleSaveRewardSettings = async () => {
    setRewardError(""); setRewardSuccess("");
    if (!selectedTemplateId) { setRewardError("لا يوجد برنامج لتعديله."); return; }
    if (!rewardForm.password) { setRewardError("يرجى إدخال كلمة المرور للتأكيد."); return; }
    if (rewardForm.rewardType === "CouponValue" && (!rewardForm.couponValue || !rewardForm.currencyCode)) {
      setRewardError("يرجى تعبئة قيمة الكوبون والعملة.");
      return;
    }
    try {
      setRewardLoading(true);
      const response = await OrganizationAxiosInstance.put(`/PassTemplate/${selectedTemplateId}/RewardSettings`, {
        CurrentPassword: rewardForm.password,
        RewardType: rewardForm.rewardType,
        CouponValue: rewardForm.rewardType === "CouponValue" ? rewardForm.couponValue : null,
        CurrencyCode: rewardForm.rewardType === "CouponValue" ? rewardForm.currencyCode : null,
      });
      setPassTemplates(prev => prev.map(p => p.id === selectedTemplateId ? response.data : p));
      setRewardSuccess("تم حفظ إعدادات المكافآت بنجاح.");
      setRewardForm(f => ({ ...f, password: "" }));
    } catch (e) {
      setRewardError(isAxiosError(e) ? (e.response?.data?.message ?? "حدث خطأ.") : "حدث خطأ.");
    } finally { setRewardLoading(false); }
  };

  const handleRenew = async () => {
    try {
      setRenewLoading(true);
      const res = await OrganizationAxiosInstance.get("/Renew");
      const url = res?.data?.paymentUrl || res?.data?.url || res?.data;
      if (url) window.location.href = url;
    } catch (e) {
      setError(isAxiosError(e) ? e.message : "Renewal error");
    } finally { setRenewLoading(false); }
  };

  if (isloading) return <Spinner loading={isloading} />;

  return (
    <div className={`space-y-5 ${activeTab === "account" ? "max-w-3xl mx-auto" : ""}`} dir="rtl">

      {/* Tab bar */}
      <div className="flex bg-white rounded-2xl border border-brand-100 shadow-sm p-1.5 gap-1">
        {([
          { key: "account", label: "الحساب",          icon: <MdAccountCircle className="w-4 h-4" /> },
          { key: "plan",    label: "الاشتراك والباقات", icon: <MdPayments className="w-4 h-4" /> },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? "bg-brand-600 text-white shadow-sm"
                : "text-gray-500 hover:bg-brand-50 hover:text-brand-700"
            }`}
          >
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* ── Account tab ── */}
      {activeTab === "account" && (
        <div className="space-y-4">

          {/* Info card */}
          <SectionCard icon={<MdBusiness className="w-4 h-4" />} title="معلومات المنشأة">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: <MdBusiness className="w-3.5 h-3.5" />,  label: "المنشأة",   value: org?.name ?? "—" },
                { icon: <MdEmail    className="w-3.5 h-3.5" />,  label: "البريد",    value: org?.email ?? "—" },
                { icon: <MdPhone    className="w-3.5 h-3.5" />,  label: "الهاتف",    value: org?.phone ?? "—" },
                { icon: <MdVerified className="w-3.5 h-3.5" />,  label: "التحقق",    value: org?.isVerified ? "مُفعّل ✓" : "غير مُفعّل" },
              ].map(({ icon, label, value }) => (
                <div key={label} className="bg-brand-50 rounded-xl px-4 py-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-gray-400">{icon}<span className="text-xs">{label}</span></div>
                  <p className="text-sm font-semibold text-gray-900 truncate">{value}</p>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Change email */}
          <SectionCard icon={<MdEmail className="w-4 h-4" />} title="تغيير البريد الإلكتروني">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField label="البريد الإلكتروني الجديد" type="email"     value={emailForm.newEmail}  onChange={v => setEmailForm(f => ({ ...f, newEmail: v }))}  placeholder="example@domain.com" />
                <InputField label="كلمة المرور للتأكيد"      type="password"  value={emailForm.password}  onChange={v => setEmailForm(f => ({ ...f, password: v }))}  placeholder="••••••••" />
              </div>
              <Feedback error={emailError} success={emailSuccess} />
              <SaveButton loading={emailLoading} label="حفظ البريد الإلكتروني" onClick={handleChangeEmail} />
            </div>
          </SectionCard>

          {/* Change password */}
          <SectionCard icon={<MdLock className="w-4 h-4" />} title="تغيير كلمة المرور">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <InputField label="كلمة المرور الحالية" type="password" value={pwForm.current}  onChange={v => setPwForm(f => ({ ...f, current: v }))}  placeholder="••••••••" />
                <InputField label="كلمة المرور الجديدة" type="password" value={pwForm.next}     onChange={v => setPwForm(f => ({ ...f, next: v }))}     placeholder="••••••••" />
                <InputField label="تأكيد كلمة المرور"   type="password" value={pwForm.confirm}  onChange={v => setPwForm(f => ({ ...f, confirm: v }))}  placeholder="••••••••" />
              </div>
              <Feedback error={pwError} success={pwSuccess} />
              <SaveButton loading={pwLoading} label="حفظ كلمة المرور" onClick={handleChangePassword} />
            </div>
          </SectionCard>

          {/* Reward settings */}
          {passTemplates.length > 0 && (
            <SectionCard icon={<MdCardGiftcard className="w-4 h-4" />} title="إعدادات المكافآت">
              <div className="space-y-4">
                {passTemplates.length > 1 && (
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-gray-500">البرنامج</label>
                    <select
                      value={selectedTemplateId}
                      onChange={e => handleSelectTemplate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                    >
                      {passTemplates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-gray-500">نوع المكافأة</label>
                  <select
                    value={rewardForm.rewardType}
                    onChange={e => setRewardForm(f => ({ ...f, rewardType: e.target.value as "Stamp" | "CouponValue" }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                  >
                    <option value="Stamp">بطاقة ختم (مثال: اجمع 4 نقاط للحصول على قهوة مجانية)</option>
                    <option value="CouponValue">قيمة كوبون (مثال: اجمع 100 نقطة للحصول على كوبون بقيمة 25 ريال)</option>
                  </select>
                </div>

                {rewardForm.rewardType === "CouponValue" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField
                      label="قيمة الكوبون لكل مكافأة"
                      type="number"
                      value={String(rewardForm.couponValue)}
                      onChange={v => setRewardForm(f => ({ ...f, couponValue: Number(v) }))}
                    />
                    <InputField
                      label="العملة"
                      value={rewardForm.currencyCode}
                      onChange={v => setRewardForm(f => ({ ...f, currencyCode: v.toUpperCase() }))}
                      placeholder="SAR"
                    />
                  </div>
                )}

                <InputField
                  label="كلمة المرور للتأكيد"
                  type="password"
                  value={rewardForm.password}
                  onChange={v => setRewardForm(f => ({ ...f, password: v }))}
                  placeholder="••••••••"
                />

                <Feedback error={rewardError} success={rewardSuccess} />
                <SaveButton loading={rewardLoading} label="حفظ إعدادات المكافآت" onClick={handleSaveRewardSettings} />
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* ── Plans tab ── */}
      {activeTab === "plan" && (
        <div className="space-y-5">

          {/* Current plan banner */}
          {org?.planName && (
            <div className={`rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
              isExpired ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"
            }`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 mb-0.5">باقتك الحالية</p>
                <p className="text-lg font-black text-gray-900">{org.planName}</p>
                {expiryDate && (
                  <p className={`text-xs mt-1 ${isExpired ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                    {isExpired ? "⚠ انتهت في " : "تنتهي في "}
                    {expiryDate.toLocaleDateString("ar-SA")}
                  </p>
                )}
              </div>
              {isExpired && (
                <button
                  onClick={handleRenew}
                  disabled={renewLoading}
                  className="px-5 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-50 transition flex-shrink-0"
                >
                  {renewLoading ? "جارٍ المعالجة..." : "تجديد الاشتراك"}
                </button>
              )}
            </div>
          )}

          <div className="text-center">
            <h2 className="text-xl font-black text-gray-900">اختر الباقة المناسبة</h2>
            <p className="text-sm text-gray-400 mt-1">يمكنك الترقية في أي وقت</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 items-start">
            {grouped.map((g) => {
              const yearly = g.yearly;
              const current = !isFreeTrialOrg && organizationPlanId === yearly?.id;
              const isExpiredCurrent = current && isExpired;

              const tierMeta: Record<string, { label: string; desc: string }> = {
                Silver:   { label: "الباقة الفضية 🥈",    desc: "مناسبة للمشاريع الصغيرة وبدء التشغيل" },
                Gold:     { label: "الباقة الذهبية 🥇",   desc: "ميزات متقدمة لنمو نشاطك" },
                Platinum: { label: "الباقة البلاتينية 💎", desc: "أقصى مرونة للفروع المتعددة" },
              };
              const meta = tierMeta[g.tier] ?? { label: g.tier, desc: "" };

              const features: { ok: boolean; text: React.ReactNode }[] = [
                { ok: true, text: <span>عدد المواقع في الخريطة: <strong>{yearly?.allowedLocations ?? "—"}</strong></span> },
                { ok: true, text: <span>عدد التصاميم للبطاقة: <strong>{yearly?.allowedPasstemplates ?? "—"}</strong></span> },
                { ok: true, text: "إشعارات مجانية ولا محدودة" },
                ...(g.tier === "Silver" ? [
                  { ok: false, text: "مستخدمون فرعيون" },
                  { ok: false, text: "تعديل تصميم البطاقة" },
                  { ok: false, text: "تسجيل عملاء بدون تكرار" },
                  { ok: false, text: "تحميل/تصدير بيانات العملاء" },
                ] : []),
                ...(g.tier === "Gold" ? [
                  { ok: true,  text: <span>مستخدمون فرعيون: <strong>{yearly?.allowedSubusers ?? "—"}</strong></span> },
                  { ok: true,  text: "تعديل تصميم البطاقة" },
                  { ok: false, text: "تسجيل عملاء بدون تكرار" },
                  { ok: false, text: "تحميل/تصدير بيانات العملاء" },
                ] : []),
                ...(g.tier === "Platinum" ? [
                  { ok: true, text: <span>مستخدمون فرعيون: <strong>{yearly?.allowedSubusers ?? "—"}</strong></span> },
                  { ok: true, text: "تعديل تصميم البطاقة" },
                  { ok: true, text: "تسجيل عملاء بدون تكرار" },
                  { ok: true, text: "تحميل/تصدير بيانات العملاء" },
                ] : []),
              ];

              const waMessage = encodeURIComponent(`مرحباً، أريد الاشتراك في ${meta.label}`);
              const waUrl = `https://wa.me/966553608481?text=${waMessage}`;

              return (
                <div
                  key={g.tier}
                  className={`relative bg-white border rounded-[22px] px-7 py-8 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                    g.featured
                      ? "border-brand-400 shadow-lg scale-[1.035] bg-gradient-to-b from-brand-50/60 to-white"
                      : current
                      ? "border-emerald-300 shadow-md"
                      : "border-brand-100 shadow-sm"
                  }`}
                >
                  {/* Most popular badge */}
                  {g.featured && (
                    <span className="absolute -top-3.5 end-7 bg-brand-600 text-white text-xs font-extrabold px-4 py-1 rounded-full shadow-sm">
                      الأكثر اختياراً ⭐
                    </span>
                  )}

                  {/* Current plan badge */}
                  {current && (
                    <span className={`absolute -top-3.5 start-7 text-white text-xs font-extrabold px-4 py-1 rounded-full shadow-sm ${isExpired ? "bg-red-500" : "bg-emerald-500"}`}>
                      {isExpired ? "منتهية ⚠" : "باقتك الحالية ✓"}
                    </span>
                  )}

                  {/* Tier name */}
                  <p className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2.5">{meta.label}</p>

                  {/* Price */}
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-5xl font-black text-gray-900 leading-none">{yearly?.price?.toLocaleString("en-US") ?? "—"}</span>
                    <span className="text-lg font-bold text-gray-400 mb-1"> ر.س</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-1">سنوياً</p>
                  <p className="text-sm text-gray-500 mb-5 leading-relaxed">{meta.desc}</p>

                  <hr className="border-brand-100 mb-5" />

                  {/* Features */}
                  <ul className="flex-1 space-y-0 mb-6">
                    {features.map((f, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-2.5 text-sm py-2.5 border-b border-brand-50 last:border-0 ${!f.ok ? "opacity-40" : "text-gray-700"}`}
                      >
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black mt-0.5 ${
                          f.ok ? "bg-brand-100 text-brand-600" : "bg-gray-100 text-gray-400"
                        }`}>
                          {f.ok ? "✓" : "✕"}
                        </span>
                        {f.text}
                      </li>
                    ))}
                  </ul>

                  {/* Expiry info */}
                  {current && expiryDate && (
                    <p className="text-xs text-center mb-3 text-gray-400">
                      {isExpired ? "⚠ انتهت في " : "تنتهي في "}
                      <span className="font-semibold text-gray-600">{expiryDate.toLocaleDateString("ar-SA")}</span>
                    </p>
                  )}

                  {/* CTA */}
                  {isExpiredCurrent ? (
                    <button
                      onClick={handleRenew}
                      disabled={renewLoading}
                      className="w-full py-3 rounded-xl text-sm font-bold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-all active:scale-95"
                    >
                      {renewLoading ? "جارٍ المعالجة..." : "تجديد الاشتراك"}
                    </button>
                  ) : current ? (
                    <div className="w-full py-3 rounded-xl text-sm font-bold text-center bg-gray-100 text-gray-400 cursor-default">
                      باقتك الحالية
                    </div>
                  ) : (
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={`w-full py-3 rounded-xl text-sm font-bold text-center transition-all active:scale-95 block ${
                        g.featured
                          ? "bg-brand-600 text-white hover:bg-brand-700 shadow-md hover:shadow-lg"
                          : "bg-transparent text-brand-600 border-[1.5px] border-brand-300 hover:bg-brand-50"
                      }`}
                    >
                      اشترك الآن
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {error && <p className="text-center text-sm text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default Settings;
